import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Xmark } from '@gravity-ui/icons';
import CameraHierarchyFilter, { EMPTY_HIERARCHY } from '../../../components/CameraHierarchyFilter/CameraHierarchyFilter';
import MetricThresholdInput, { coerceThreshold } from './MetricThresholdInput';
import {
  createViolationRuleV2,
  getViolationRuleCatalog,
  getViolationRuleV2,
  updateViolationRuleActivation,
  updateViolationRuleCategory,
  updateViolationRuleCore,
  useRuleCategoriesSupported,
} from '../../../services/violations';
import { getCamera } from '../../../services/cameras';
import { getDetectionProfile } from '../../../services/detectionProfile';
import ActivationEditor from './ActivationEditor';
import ZoneSelector from './ZoneSelector';
import RetentionFields from './RetentionFields';
import RecurrenceFields from './RecurrenceFields';
import CooldownFields from './CooldownFields';
import SamplingBoostFields from './SamplingBoostFields';
import { categoryTransferWarning, RuleCategoryField } from './BusinessRuleFields';
import RuleLifecycleField from './RuleLifecycleField';
import { isAdmin } from '../../../services/api';
import {
  deleteSamplingBoost,
  getSamplingBoost,
  getSamplingEffective,
  putSamplingBoost,
} from '../../../services/samplingBoost';
import { cooldownFromRule, cooldownToDto, validateCooldown } from '../../../utils/ruleCooldown';
import { boostFromResponse, planBoostSave, validateBoost } from '../../../utils/ruleSamplingBoost';
import { resolveCameraHierarchy } from '../../../utils/resolveCameraHierarchy';
import {
  ACTIVATION_ALWAYS,
  ACTIVATION_SCHEDULED,
  toActivationDto,
  validateActivation,
} from '../../../utils/violationSchedule';
import {
  EMPTY_RETENTION,
  toRetentionDto,
  validateRetention,
} from '../../../utils/violationRetention';
import {
  EMPTY_ZONE_SELECTION,
  ZONE_SCOPE_SPECIFIC,
  fromRuleZones,
  toZonesDto,
  validateZoneSelection,
} from '../../../utils/ruleZones';
import {
  EMPTY_RECURRENCE,
  fromRuleRecurrence,
  toRecurrenceDto,
  validateRecurrence,
} from '../../../utils/ruleRecurrence';
import { describeUserError } from '../../../utils/apiError';
import { describeConflict } from '../../../utils/conflictMessages';
import { getOperatorLabel, getValueTypeControl } from '../../../constants/violationConstants';
import { formatRequiredFeatures, getFeatureDisplayName } from '../../../constants/analyticsConstants';
import { withCameraWord } from '../../../utils/hierarchyLabels';
import {
  RULE_CATEGORY_VIOLATION,
  ruleCategoryOf,
} from '../../../utils/businessMetrics';
import { ruleFieldErrorsFrom422 } from '../../../utils/ruleFieldErrors';
import { deriveRuleState, RULE_STATE } from '../../../constants/violationConstants';
import { APP_EVENTS, emitAppEvent } from '../../../utils/appEvents';

/*
 * Создание правила идёт на V2 (Part 4, раздел 3.4). Контракт POST V2 поля
 * activation не содержит, поэтому расписание сохраняется вторым запросом
 * PUT /admin/violation-rules/{rule_id}/activation и только в режиме scheduled:
 * для режима «всегда» backend и так хранит legacy-поведение.
 *
 * Если правило создалось, а расписание — нет, повторная отправка формы НЕ
 * создаёт второе правило: созданное запоминается в createdRuleRef.
 *
 * Ускорение детекций (Sampling Boost, ТЗ №39) — тоже отдельный контракт:
 * PUT/DELETE /admin/violation-rules/{rule_id}/sampling-boost после core.
 * Порядок create: POST правила (с паузой cooldown_*) → PUT ускорения →
 * расписание. Если правило создано, а ускорение или расписание — нет, форма
 * остаётся открытой, показывает ошибку по каждому шагу отдельно и переходит
 * в редактирование созданного правила: повторное «Сохранить» делает PUT core
 * и повторяет только несохранённые шаги, второго POST не бывает.
 * onPartiallySaved сообщает родителю, что правило уже записано (таблица
 * перечитывается), но форма ещё не закрыта.
 *
 * BM-FE-TZ-002C: категория «Нарушение / Бизнес-метрика» — единственное
 * бизнес-поле правила. При создании она уходит в POST (category), core-PUT
 * её не принимает: смена категории существующего правила — отдельный
 * идемпотентный PATCH …/{id}/category после core, отключать правило для
 * этого не нужно. Пока backend категорий не знает
 * (useRuleCategoriesSupported), category в запрос не попадает вовсе.
 * Ошибки сохранения (§7): 422 — подсветка полей; 409 — правило перечитывается,
 * повторной записи нет; 503 — введённые данные остаются в форме, есть «Повторить».
 */

/**
 * mode:
 *  - `direct`  — правило создаётся сразу (администратор или client_and_admin);
 *  - `propose` — правило уходит заявкой, production-список не меняется до
 *    одобрения администратора (ТЗ 7.1);
 *  - `edit`    — редактирование core-полей существующего правила (ТЗ задачи
 *    2–4): PUT /admin/violation-rules-v2/{rule_id}, без Retention/Activation
 *    и без активации правила — это отдельные операции с отдельными PUT.
 */
const ViolationRuleFormDrawer = ({
  onClose,
  onCreated,
  onSaved,
  onPartiallySaved,
  mode = 'direct',
  rule = null,
  onPropose,
  cameraId: fixedCameraId = null,
  catalogRefreshToken = 0,
  cameraLabel = '',
}) => {
  const isEdit = mode === 'edit';
  // FE-16: смена категории готового правила — только у администратора.
  const categoryLocked = isEdit && !isAdmin();

  const [hierarchy, setHierarchy] = useState(() => (
    isEdit ? { ...EMPTY_HIERARCHY, cameraId: String(rule.camera_id) } : EMPTY_HIERARCHY
  ));
  const [catalog, setCatalog] = useState(null);
  const [catalogState, setCatalogState] = useState('idle'); // idle | loading | ready | error
  const [catalogError, setCatalogError] = useState('');

  const [name, setName] = useState(() => (isEdit ? rule.name ?? '' : ''));
  const [metric, setMetric] = useState(() => (isEdit ? rule.metric ?? '' : ''));
  const [operator, setOperator] = useState(() => (isEdit ? rule.operator ?? '' : ''));
  const [threshold, setThreshold] = useState(() => (isEdit ? String(rule.threshold ?? '') : ''));
  // FE-FIX29: область контроля — это пара (scope, zoneIds), а не одиночный id.
  const [zoneSelection, setZoneSelection] = useState(() => (
    isEdit ? fromRuleZones(rule) : { ...EMPTY_ZONE_SELECTION }
  ));
  const [durationSeconds, setDurationSeconds] = useState(() => (
    isEdit ? String(rule.duration_seconds ?? '0') : '0'
  ));
  const [allowedGapSeconds, setAllowedGapSeconds] = useState(() => (
    isEdit ? String(rule.allowed_gap_seconds ?? '0') : '0'
  ));
  const [retention, setRetention] = useState(EMPTY_RETENTION);

  const categoriesSupported = useRuleCategoriesSupported();
  const [category, setCategory] = useState(() => (isEdit ? ruleCategoryOf(rule) : RULE_CATEGORY_VIOLATION));
  // Категория, записанная на backend: от неё решается, нужен ли PATCH.
  const savedCategoryRef = useRef(isEdit ? ruleCategoryOf(rule) : null);
  // 503: форма уже хранит черновик в памяти, остаётся предложить повтор.
  const [retryable, setRetryable] = useState(false);
  const [featureNames, setFeatureNames] = useState({});

  /*
   * FE-FIX17 / FE-FIX24: две независимые durable политики правила. Обе имеют
   * backend-default true, поэтому новая форма стартует со включёнными
   * галочками, а edit гидратируется строго из ответа правила: отсутствующее
   * поле у старого правила означает тот же default true, а не «выключено».
   */
  const [includeInAnalytics, setIncludeInAnalytics] = useState(() => (
    isEdit ? rule.include_in_analytics !== false : true
  ));
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => (
    isEdit ? rule.notifications_enabled !== false : true
  ));

  // FE-FIX30: политика повторяемости. Пустая тройка = системная политика backend.
  const [recurrence, setRecurrence] = useState(() => (
    isEdit ? fromRuleRecurrence(rule) : { ...EMPTY_RECURRENCE }
  ));

  const [activation, setActivation] = useState({
    mode: ACTIVATION_ALWAYS, timezone: '', windows: [],
  });

  // ТЗ №42: пауза — core-поле правила, гидратируется из ответа.
  const [cooldown, setCooldown] = useState(() => cooldownFromRule(isEdit ? rule : null));

  /*
   * ТЗ №39: ускорение детекций. В propose-режиме правила ещё нет, а заявка
   * несёт только core-поля, поэтому блок не показывается. boostBaselineRef —
   * последняя сохранённая конфигурация: по ней решается, нужен ли PUT/DELETE.
   */
  const boostAvailable = mode !== 'propose';
  const [boost, setBoost] = useState(() => boostFromResponse(null));
  const [boostLoadState, setBoostLoadState] = useState(isEdit ? 'loading' : 'ready');
  const boostBaselineRef = useRef(null);
  const [effective, setEffective] = useState({ state: 'idle', data: null });

  const [fieldErrors, setFieldErrors] = useState({});
  const [retentionErrors, setRetentionErrors] = useState({});
  const [recurrenceErrors, setRecurrenceErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [stepErrors, setStepErrors] = useState({ category: '', boost: '', schedule: '' });
  const [submitting, setSubmitting] = useState(false);
  // Состояние submitting применяется только после рендера: второй submit в
  // том же тике его ещё не видит. Реф закрывает окно для двойного POST.
  const submittingRef = useRef(false);
  // Имя камеры из предзаполнения иерархии — для строки камеры без cameraLabel.
  const [resolvedCameraName, setResolvedCameraName] = useState('');

  const createdRuleRef = useRef(null);
  // Правило уже создано, а один из следующих шагов нет: форма редактирует его.
  const [createdRule, setCreatedRule] = useState(null);
  const scheduleSavedRef = useRef(false);
  const editingExisting = isEdit || Boolean(createdRule);
  // Первый рендер edit-формы уже содержит камеру правила — сброс
  // camera-dependent полей ниже должен сработать только на реальную смену
  // камеры пользователем, а не на инициализацию (ТЗ 35).
  const initialCameraRef = useRef(isEdit ? String(rule.camera_id) : null);

  const cameraId = fixedCameraId ?? hierarchy.cameraId;
  // Отдельный от initialCameraRef реф: тот гасит только самый первый прогон
  // edit-формы, а этот отличает реальную смену камеры от простого перечитывания
  // каталога по catalogRefreshToken (ТЗ 35, дополнение Task 14).
  const previousCameraIdRef = useRef(cameraId);

  useEffect(() => {
    const cameraChanged = previousCameraIdRef.current !== cameraId;
    previousCameraIdRef.current = cameraId;

    if (initialCameraRef.current === String(cameraId)) {
      initialCameraRef.current = null;
    } else if (cameraChanged) {
      // Смена камеры делает старые выборы метрики/условия/зоны бессмысленными:
      // они чистятся синхронно, до запроса нового каталога (ТЗ 35). Перечитывание
      // каталога той же камеры (catalogRefreshToken) этот выбор не трогает.
      setMetric('');
      setOperator('');
      setThreshold('');
      setZoneSelection({ ...EMPTY_ZONE_SELECTION });
    }

    if (!cameraId) {
      setCatalog(null);
      setCatalogState('idle');
      return undefined;
    }

    let cancelled = false;
    setCatalog(null);
    setCatalogState('loading');
    setCatalogError('');

    getViolationRuleCatalog(cameraId)
      .then((data) => {
        if (cancelled) return;
        setCatalog(data);
        setCatalogState('ready');
        // Зона могла исчезнуть из каталога (удалена через ZoneEditor, пока форма
        // была открыта) — выбор, ссылающийся в никуда, сбрасывается сам. Режим
        // «конкретные зоны» при этом сохраняется: форма попросит выбрать зону
        // заново, а не подменит смысл правила общей областью контроля.
        setZoneSelection((prev) => {
          if (prev.scope !== ZONE_SCOPE_SPECIFIC || prev.zoneIds.length === 0) return prev;
          const zones = Array.isArray(data?.zones) ? data.zones : [];
          const kept = prev.zoneIds.filter(
            (zoneId) => zones.some((zone) => zone.zone_id === zoneId)
          );
          return kept.length === prev.zoneIds.length ? prev : { ...prev, zoneIds: kept };
        });
      })
      .catch((error) => {
        if (cancelled) return;
        const status = error?.response?.status;
        setCatalog(null);
        setCatalogError(
          status === 409
            ? 'Камера недоступна для настройки правила'
            : describeUserError(error, 'Не удалось загрузить каталог метрик')
        );
        setCatalogState('error');
      });

    return () => { cancelled = true; };
  }, [cameraId, catalogRefreshToken]);

  /**
   * Названия детекций для подсказки «будет использована детекция: …» (ТЗ 6.1).
   * Состав фич правило получает из каталога; профиль нужен только чтобы
   * показать их по-русски. Профиль недоступен — подсказка просто покажет
   * идентификаторы, форма от этого не ломается.
   */
  useEffect(() => {
    if (!cameraId) {
      setFeatureNames({});
      return undefined;
    }

    let cancelled = false;
    getDetectionProfile(cameraId)
      .then((profile) => {
        if (cancelled) return;
        const names = {};
        (profile?.features || []).forEach((feature) => {
          names[feature.feature_id] = getFeatureDisplayName(feature);
        });
        setFeatureNames(names);
      })
      .catch(() => { if (!cancelled) setFeatureNames({}); });

    return () => { cancelled = true; };
  }, [cameraId]);

  // Часовой пояс по умолчанию — зона камеры; пользователь может сменить вручную.
  // В edit расписание не редактируется этой формой — таймзона не нужна.
  useEffect(() => {
    if (isEdit || !cameraId) return undefined;
    let cancelled = false;
    getCamera(cameraId)
      .then((camera) => {
        if (cancelled || !camera?.display_timezone) return;
        setActivation((prev) => (prev.timezone ? prev : { ...prev, timezone: camera.display_timezone }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isEdit, cameraId]);

  /*
   * ТЗ №04/FIX-04: в edit каскад предзаполняется путём камеры правила.
   * Камера при этом не меняется, поэтому сброс метрики/зоны выше не срабатывает.
   * Если путь не нашёлся, форма остаётся с одной камерой, как раньше.
   */
  useEffect(() => {
    if (!isEdit || fixedCameraId) return undefined;
    let cancelled = false;
    const ruleCameraId = String(rule.camera_id);
    resolveCameraHierarchy(rule.camera_id).then((resolved) => {
      if (cancelled || !resolved) return;
      setResolvedCameraName(resolved.cameraName || '');
      // Пользователь мог уже выбрать другую камеру — его выбор не перетираем.
      setHierarchy((prev) => (prev.cameraId === ruleCameraId && !prev.roomId ? resolved.value : prev));
    });
    return () => { cancelled = true; };
    // Правило формы не меняется за время её жизни.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Конфигурация ускорения существующего правила; 404 сервис уже превращает в null.
  useEffect(() => {
    if (!isEdit || !boostAvailable) return undefined;
    let cancelled = false;
    Promise.resolve()
      .then(() => getSamplingBoost(rule.rule_id))
      .then((config) => {
        if (cancelled) return;
        boostBaselineRef.current = config || null;
        setBoost(boostFromResponse(config || null));
        setBoostLoadState('ready');
      })
      .catch(() => { if (!cancelled) setBoostLoadState('error'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Блок «Камера сейчас»: текущий интервал опроса выбранной камеры.
  useEffect(() => {
    if (!boostAvailable || !cameraId) {
      setEffective({ state: 'idle', data: null });
      return undefined;
    }
    let cancelled = false;
    setEffective({ state: 'loading', data: null });
    Promise.resolve()
      .then(() => getSamplingEffective(cameraId))
      .then((data) => { if (!cancelled) setEffective({ state: data ? 'ready' : 'error', data: data || null }); })
      .catch(() => { if (!cancelled) setEffective({ state: 'error', data: null }); });
    return () => { cancelled = true; };
  }, [boostAvailable, cameraId]);

  const metrics = useMemo(
    () => (Array.isArray(catalog?.metrics) ? catalog.metrics : []),
    [catalog]
  );

  // Зоны камеры целиком берутся из каталога (ТЗ 35) — ни slice, ни dedup,
  // ни фоллбэк на отдельный эндпоинт зон здесь больше не нужны.
  const zones = useMemo(
    () => (Array.isArray(catalog?.zones) ? catalog.zones : []),
    [catalog]
  );

  const selectedMetric = useMemo(
    () => metrics.find((item) => item.metric === metric) || null,
    [metrics, metric]
  );

  const requiredFeaturesText = useMemo(
    () => formatRequiredFeatures(selectedMetric?.required_features, featureNames),
    [selectedMetric, featureNames]
  );

  const availableOperators = useMemo(
    () => (Array.isArray(selectedMetric?.operators) ? selectedMetric.operators : []),
    [selectedMetric]
  );

  const validate = () => {
    const errors = {};

    if (!cameraId) errors.camera = 'Выберите камеру';
    if (!name.trim()) errors.name = 'Укажите название';
    else if (name.trim().length > 255) errors.name = 'Не длиннее 255 символов';
    if (!metric) errors.metric = 'Выберите контролируемый параметр';
    if (!operator) errors.operator = 'Выберите условие';

    const control = getValueTypeControl(selectedMetric?.value_type);
    if (threshold === '') {
      errors.threshold = 'Укажите значение';
    } else if (control === 'integer' && !Number.isInteger(Number(threshold))) {
      errors.threshold = 'Значение должно быть целым числом';
    } else if (control === 'number' && !Number.isFinite(Number(threshold))) {
      errors.threshold = 'Значение должно быть конечным числом';
    }

    const duration = Number(durationSeconds);
    if (!Number.isInteger(duration) || duration < 0) {
      errors.duration = 'Целое число секунд, не меньше нуля';
    }

    const gap = Number(allowedGapSeconds);
    if (!Number.isInteger(gap) || gap < 0) {
      errors.gap = 'Целое число секунд, не меньше нуля';
    }

    const zoneValidation = validateZoneSelection(zoneSelection);
    if (!zoneValidation.valid) errors.zone = zoneValidation.error;

    // Пауза (ТЗ №42) — core-поле, проверяется в обоих режимах.
    const cooldownError = validateCooldown(cooldown);
    if (cooldownError) errors.cooldown = cooldownError;

    // Ускорение проверяется, только если оно будет сохраняться этой формой.
    if (boostAvailable && boostLoadState === 'ready') {
      const boostError = validateBoost(boost);
      if (boostError) errors.boost = boostError;
    }

    // Recurrence входит в core-правило, поэтому проверяется в обоих режимах.
    const recurrenceValidation = validateRecurrence(recurrence);
    setRecurrenceErrors(recurrenceValidation.errors);

    // Activation и Retention в edit-режиме этой формой не редактируются —
    // у них свои PUT (activation, retention), проверять их здесь нечего.
    // Сроки хранения уходят в POST, поэтому после создания тоже не проверяются;
    // расписание проверяется, пока оно ещё не сохранено.
    let retentionValid = true;
    if (!editingExisting) {
      const retentionValidation = validateRetention(retention);
      setRetentionErrors(retentionValidation.errors);
      retentionValid = retentionValidation.valid;
    }
    if (!isEdit && !scheduleSavedRef.current) {
      const activationValidation = validateActivation(activation);
      if (!activationValidation.valid) {
        errors.activation = 'Проверьте расписание активации';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0 && retentionValid && recurrenceValidation.valid;
  };

  const coreFields = () => ({
    name: name.trim(),
    camera_id: Number(cameraId),
    // FE-FIX29 / ТЗ №03: specific — только zone_ids, general — zone_ids=[] и zone_id=null.
    ...toZonesDto(zoneSelection),
    metric,
    operator,
    threshold: coerceThreshold(threshold, selectedMetric?.value_type),
    duration_seconds: Number(durationSeconds),
    allowed_gap_seconds: Number(allowedGapSeconds),
    include_in_analytics: includeInAnalytics,
    notifications_enabled: notificationsEnabled,
    ...toRecurrenceDto(recurrence),
    ...cooldownToDto(cooldown),
  });

  // Только для POST: core-PUT category не принимает. Без поддержки категорий
  // — пустой объект, старый backend лишних полей не примет.
  const categoryField = () => (categoriesSupported ? { category } : {});

  /**
   * Общая обработка отказа core-записи (§7). Возвращает true, если ошибка
   * разобрана здесь; иначе вызывающий показывает свой текст.
   */
  const handleCoreError = async (error, existing) => {
    const status = error?.response?.status;
    if (status === 422) {
      const mapped = ruleFieldErrorsFrom422(error);
      if (mapped.matched) {
        setFieldErrors(mapped.form);
        setSubmitError('Проверьте выделенные поля');
        return true;
      }
      return false;
    }
    if (status === 503) {
      setRetryable(true);
      setSubmitError('Система временно недоступна. Введённые данные сохранены в форме — повторите попытку.');
      return true;
    }
    if (status === 409 && existing) {
      // Не повторяем запись сами: перечитываем правило и объясняем, что случилось.
      const conflict = describeConflict(error, 'rule_core');
      try {
        const fresh = await getViolationRuleV2(existing.rule_id);
        if (fresh && deriveRuleState(fresh) === RULE_STATE.ACTIVE) {
          setSubmitError('Правило активировали, пока форма была открыта. Деактивируйте его, чтобы изменить.');
          return true;
        }
      } catch (refetchError) {
        // Перечитать не удалось — остаётся текст конфликта.
      }
      setSubmitError(conflict.message);
      return true;
    }
    return false;
  };

  /**
   * Шаг смены категории существующего правила. Возвращает { category, error }:
   * category — записанная категория, если PATCH прошёл.
   */
  const saveCategoryStep = async (ruleId, prefix) => {
    if (!categoriesSupported || categoryLocked || savedCategoryRef.current == null || savedCategoryRef.current === category) {
      return { category: null, error: '' };
    }
    try {
      const updated = await updateViolationRuleCategory(ruleId, category);
      savedCategoryRef.current = category;
      // События правила переехали между вкладками: колокольчик нарушений и
      // сводки перечитываются сами.
      emitAppEvent(APP_EVENTS.RULE_CATEGORY_CHANGED);
      emitAppEvent(APP_EVENTS.NOTIFICATIONS_REFRESH);
      return { category: updated?.category || category, error: '' };
    } catch (error) {
      const message = describeUserError(error, '');
      return {
        category: null,
        error: message
          ? `${prefix}, но категория не изменена: ${message}. Повторите сохранение.`
          : `${prefix}, но категория не изменена. Повторите сохранение.`,
      };
    }
  };

  /** Шаг ускорения детекций. Возвращает текст ошибки или ''. */
  const saveBoostStep = async (ruleId, prefix) => {
    if (!boostAvailable || boostLoadState !== 'ready') return '';
    const plan = planBoostSave(boost, boostBaselineRef.current);
    if (!plan) return '';
    try {
      if (plan.type === 'put') {
        const saved = await putSamplingBoost(ruleId, plan.body);
        boostBaselineRef.current = saved && typeof saved === 'object' ? { ...plan.body, ...saved } : plan.body;
      } else {
        await deleteSamplingBoost(ruleId);
        boostBaselineRef.current = null;
      }
      return '';
    } catch (error) {
      // Английский или технический detail backend пользователю не показываем.
      const message = describeUserError(error, '');
      return message
        ? `${prefix}, но ускорение детекций не сохранено: ${message}. Повторите сохранение.`
        : `${prefix}, но ускорение детекций не сохранено. Повторите сохранение.`;
    }
  };

  /** Шаг расписания только что созданного правила. Возвращает { rule, error }. */
  const saveScheduleStep = async (created) => {
    if (isEdit || scheduleSavedRef.current || activation.mode !== ACTIVATION_SCHEDULED) {
      return { rule: created, error: '' };
    }
    try {
      // Расписание — отдельный контракт Part 2; у только что созданного
      // правила своей версии расписания ещё нет.
      const updatedActivation = await updateViolationRuleActivation(created.rule_id, {
        expected_schedule_version: createdRuleRef.current?.activation?.schedule_version ?? null,
        activation: toActivationDto(activation),
      });
      scheduleSavedRef.current = true;
      // PUT возвращает activation, а не правило: собираем карточку сами.
      return { rule: { ...created, activation: updatedActivation }, error: '' };
    } catch (error) {
      const message = describeUserError(error, '');
      return {
        rule: created,
        error: message
          ? `Правило создано, но расписание не сохранено: ${message}. Повторите сохранение.`
          : 'Правило создано, но расписание не сохранено. Повторите сохранение.',
      };
    }
  };

  const runSave = async () => {
    // Режим предложения: ничего не создаём, отправляем заявку и закрываем форму.
    if (mode === 'propose') {
      const payload = { ...coreFields(), ...categoryField(), ...toRetentionDto(retention) };
      try {
        await (onPropose ? onPropose('rule_create', payload) : undefined);
        onCreated(null);
      } catch (error) {
        setSubmitError(describeUserError(error, 'Не удалось отправить заявку'));
      }
      return;
    }

    const existing = isEdit ? rule : createdRuleRef.current;
    let saved;

    if (existing) {
      try {
        saved = await updateViolationRuleCore(existing.rule_id, coreFields());
      } catch (error) {
        if (await handleCoreError(error, existing)) return;
        const conflict = describeConflict(error, 'rule_core');
        setSubmitError(conflict.kind === 'not_conflict'
          ? describeUserError(error, 'Не удалось сохранить правило')
          : conflict.message);
        return;
      }
    } else {
      try {
        saved = await createViolationRuleV2({ ...coreFields(), ...categoryField(), ...toRetentionDto(retention) });
      } catch (error) {
        if (await handleCoreError(error, null)) return;
        setSubmitError(
          error?.response?.status === 409
            ? 'Камера недоступна для настройки правила'
            : describeUserError(error, 'Не удалось создать правило')
        );
        return;
      }
      createdRuleRef.current = saved;
      savedCategoryRef.current = ruleCategoryOf(saved?.category ? saved : { category });
    }

    const ruleId = existing?.rule_id ?? saved?.rule_id;
    const result = { ...(existing || {}), ...(saved || {}), rule_id: ruleId };
    const prefix = isEdit ? 'Правило сохранено' : 'Правило создано';

    const categoryStep = await saveCategoryStep(ruleId, prefix);
    if (categoryStep.category) result.category = categoryStep.category;
    const boostError = await saveBoostStep(ruleId, prefix);
    const schedule = await saveScheduleStep(result);
    setStepErrors({ category: categoryStep.error, boost: boostError, schedule: schedule.error });

    if (categoryStep.error || boostError || schedule.error) {
      // Core уже записан: дальше форма редактирует это правило, а не создаёт новое.
      if (!isEdit) setCreatedRule(createdRuleRef.current);
      if (onPartiallySaved) onPartiallySaved(schedule.rule);
      return;
    }

    if (isEdit) onSaved(schedule.rule);
    else onCreated(schedule.rule);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    setSubmitError('');
    setRetryable(false);
    if (!validate()) return;
    // ТЗ V5 №18: перенос истории — только после явного подтверждения.
    const categoryChanging = isEdit && categoriesSupported && !categoryLocked
      && savedCategoryRef.current != null && savedCategoryRef.current !== category;
    if (categoryChanging && !window.confirm(`${categoryTransferWarning(category)} Сохранить?`)) return;

    submittingRef.current = true;
    setSubmitting(true);
    try {
      await runSave();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const title = (() => {
    if (editingExisting) return 'Изменить правило';
    return mode === 'propose' ? 'Предложить правило' : 'Создать правило';
  })();

  const submitLabel = (() => {
    if (editingExisting) return submitting ? 'Сохраняем…' : 'Сохранить';
    if (submitting) return mode === 'propose' ? 'Отправляем…' : 'Создаём…';
    return mode === 'propose' ? 'Предложить правило' : 'Создать правило';
  })();

  return (
    <>
      <div className="vio-drawer-overlay" onClick={onClose} />
      <aside className="vio-drawer" role="dialog" aria-label={title}>
        <div className="vio-drawer-header">
          <h2 className="vio-drawer-title">{title}</h2>
          <button type="button" className="vio-drawer-close" onClick={onClose} aria-label="Закрыть">
            <Xmark width={20} height={20} />
          </button>
        </div>

        <form className="vio-drawer-body" onSubmit={handleSubmit} id="vio-rule-form">
          <RuleCategoryField
            value={categoriesSupported ? category : RULE_CATEGORY_VIOLATION}
            onChange={setCategory}
            supported={categoriesSupported}
            disabled={submitting}
            locked={categoryLocked}
            error={fieldErrors.category}
            idPrefix="vio-rule"
            savedValue={isEdit && categoriesSupported ? savedCategoryRef.current : null}
          />

          <div>
            <p className="vio-drawer-section-title">
              {isEdit ? 'Камера' : 'Выбор камеры'}
            </p>
            {/*
              * Finding 1: в edit-режиме hierarchy сидится как
              * {...EMPTY_HIERARCHY, cameraId} — у CameraHierarchyFilter нет
              * родительских уровней (предприятие/цех/участок/помещение), поэтому
              * он не может ни загрузить, ни показать имя текущей камеры: все
              * select'ы рисуют «Все …», а камера видна только как задизейбленный
              * пустой select. Явная строка ниже — единственное место, где
              * пользователь фактически видит, какая камера редактируется.
              * ТЗ №04: теперь каскад предзаполняется путём камеры, но строка
              * остаётся — путь может не найтись, а имя камеры нужно сразу.
              */}
            {isEdit && (
              <p className="vio-readonly-value">
                {(cameraLabel || resolvedCameraName)
                  ? withCameraWord(cameraLabel || resolvedCameraName)
                  : `Камера №${rule.camera_id}`}
              </p>
            )}
            {!fixedCameraId && (
              <div className="vio-filters-grid">
                <CameraHierarchyFilter value={hierarchy} onChange={setHierarchy} disabled={submitting} />
              </div>
            )}
            {fieldErrors.camera && <p className="vio-field-error">{fieldErrors.camera}</p>}
            {catalogState === 'error' && <p className="vio-field-error">{catalogError}</p>}
          </div>

          {cameraId && (
            <ZoneSelector
              zones={zones}
              value={zoneSelection}
              onChange={setZoneSelection}
              disabled={submitting}
              loading={catalogState === 'loading'}
              error={fieldErrors.zone}
            />
          )}

          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-rule-name">Название</label>
            <input
              id="vio-rule-name"
              type="text"
              maxLength={255}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            {fieldErrors.name && <p className="vio-field-error">{fieldErrors.name}</p>}
          </div>

          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-rule-metric">Контролируемый параметр</label>
            <select
              id="vio-rule-metric"
              value={metric}
              disabled={catalogState !== 'ready' || metrics.length === 0}
              onChange={(event) => { setMetric(event.target.value); setOperator(''); setThreshold(''); }}
            >
              <option value="">
                {catalogState === 'loading' ? 'Загружаем каталог…' : '— выберите —'}
              </option>
              {metrics.map((item) => (
                <option key={item.metric} value={item.metric}>
                  {item.display_name || item.metric}
                </option>
              ))}
            </select>
            {catalogState === 'ready' && metrics.length === 0 && (
              <p className="vio-field-hint vio-field-hint--warning">
                Для этой камеры каталог не вернул ни одной метрики.
              </p>
            )}
            {requiredFeaturesText && (
              <p className="vio-field-hint">
                Правило использует детекцию {requiredFeaturesText}
              </p>
            )}
            {fieldErrors.metric && <p className="vio-field-error">{fieldErrors.metric}</p>}
          </div>

          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-rule-operator">Условие</label>
            <select
              id="vio-rule-operator"
              value={operator}
              disabled={availableOperators.length === 0}
              onChange={(event) => setOperator(event.target.value)}
            >
              <option value="">— выберите —</option>
              {availableOperators.map((item) => (
                <option key={item} value={item}>{getOperatorLabel(item)}</option>
              ))}
            </select>
            {fieldErrors.operator && <p className="vio-field-error">{fieldErrors.operator}</p>}
          </div>

          <MetricThresholdInput
            metricItem={selectedMetric}
            value={threshold}
            onChange={setThreshold}
            error={fieldErrors.threshold}
          />

          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-rule-duration">Длительность, сек</label>
            <input
              id="vio-rule-duration"
              type="number"
              min="0"
              step="1"
              value={durationSeconds}
              onChange={(event) => setDurationSeconds(event.target.value)}
            />
            {fieldErrors.duration && <p className="vio-field-error">{fieldErrors.duration}</p>}
          </div>

          <div className="vio-field">
            <label className="vio-field-label" htmlFor="vio-rule-gap">Допустимый разрыв, сек</label>
            <input
              id="vio-rule-gap"
              type="number"
              min="0"
              step="1"
              value={allowedGapSeconds}
              onChange={(event) => setAllowedGapSeconds(event.target.value)}
            />
            {fieldErrors.gap && <p className="vio-field-error">{fieldErrors.gap}</p>}
          </div>

          <RecurrenceFields
            value={recurrence}
            onChange={setRecurrence}
            errors={recurrenceErrors}
            disabled={submitting}
            idPrefix="vio-rule"
          />

          <div className="vio-rule-extras">
            {boostAvailable && (
              <SamplingBoostFields
                value={boost}
                onChange={setBoost}
                error={fieldErrors.boost}
                disabled={submitting}
                loadState={boostLoadState}
                effective={effective}
                idPrefix="vio-rule"
              />
            )}
            <CooldownFields
              value={cooldown}
              onChange={setCooldown}
              error={fieldErrors.cooldown}
              disabled={submitting}
              idPrefix="vio-rule"
            />
          </div>


          {/*
            * FE-FIX17 / FE-FIX24. Политики независимы: аналитика не выключает
            * рассылку, рассылка не выключает аналитику, и ни одна из них не
            * скрывает сами события и кадры-доказательства.
            */}
          <RuleLifecycleField idPrefix="vio-rule" />

          <fieldset className="vio-rule-policies">
            <legend className="vio-drawer-section-title">Политики правила</legend>

            <label className="vio-checkbox-row">
              <input
                type="checkbox"
                checked={includeInAnalytics}
                disabled={submitting}
                onChange={(event) => setIncludeInAnalytics(event.target.checked)}
              />
              <span>Учитывать события по этому правилу в аналитике</span>
            </label>

            <label className="vio-checkbox-row">
              <input
                type="checkbox"
                checked={notificationsEnabled}
                disabled={submitting}
                onChange={(event) => setNotificationsEnabled(event.target.checked)}
              />
              <span>Участвует в рассылке уведомлений</span>
            </label>
          </fieldset>

          {/* Сроки хранения уходят в POST: у созданного правила они уже записаны. */}
          {!editingExisting && (
            <RetentionFields
              value={retention}
              onChange={setRetention}
              errors={retentionErrors}
              disabled={submitting}
              idPrefix="vio-rule"
            />
          )}

          {!isEdit && !scheduleSavedRef.current && (
            <>
              <ActivationEditor value={activation} onChange={setActivation} disabled={submitting} />
              {fieldErrors.activation && <p className="vio-field-error">{fieldErrors.activation}</p>}
            </>
          )}

          {submitError && <p className="vio-field-error">{submitError}</p>}
          {retryable && !submitting && (
            <button type="submit" className="vio-btn">Повторить</button>
          )}
          {stepErrors.category && <p className="vio-field-error">{stepErrors.category}</p>}
          {stepErrors.boost && <p className="vio-field-error">{stepErrors.boost}</p>}
          {stepErrors.schedule && <p className="vio-field-error">{stepErrors.schedule}</p>}
        </form>

        <div className="vio-drawer-footer">
          <button type="button" className="vio-btn" onClick={onClose} disabled={submitting}>Отмена</button>
          <button type="submit" form="vio-rule-form" className="vio-btn vio-btn--primary" disabled={submitting}>
            {submitLabel}
          </button>
        </div>
      </aside>
    </>
  );
};

export default ViolationRuleFormDrawer;
